import { useTranslate } from '@ur-solutions/react-hooks'
import {
  absenceRequestStatuses,
  type AbsenceRequestStatus,
  type AbsenceRequestStatusOrEmpty,
} from '@/modules/timesheets/absence-status.mapping'
import type { ComboboxCommonProps, ComboboxItem } from './combobox'
import { Combobox } from './combobox'

interface CommonProps<T extends string> extends ComboboxCommonProps<T> {
  exclude?: AbsenceRequestStatus[]
}

interface SingleProps extends CommonProps<AbsenceRequestStatusOrEmpty> {
  mode: 'single'
  value: AbsenceRequestStatusOrEmpty
  onChange(value: AbsenceRequestStatusOrEmpty): void
}

interface MultiProps extends CommonProps<AbsenceRequestStatus> {
  mode: 'multiple'
  value: AbsenceRequestStatus[]
  onChange(value: AbsenceRequestStatus[]): void
}

type Props = SingleProps | MultiProps

export function AbsenceStatusCombobox({
  mode,
  value,

  exclude = [],

  placeholder,
  searchPlaceholder,
  noResultsText,

  onChange,

  ...props
}: Props) {
  const translations = useTranslate({
    approved: 'timesheets.absence-approved',
    pending: 'timesheets.absence-pending',
    rejected: 'timesheets.absence-rejected',

    single: {
      placeholder: 'components.combobox.absence-status.placeholder',
    },
    multiple: {
      placeholder: 'components.multi-combobox.absence-status.placeholder',
    },
  })

  const absenceStatusItems: ComboboxItem<AbsenceRequestStatus>[] =
    absenceRequestStatuses
      .map(value => {
        if (value === 'approved') {
          return {
            value,
            label: translations.approved,
          }
        }
        if (value === 'pending') {
          return {
            value,
            label: translations.pending,
          }
        }
        return {
          value,
          label: translations.rejected,
        }
      })
      .filter(item => !exclude.includes(item.value))

  if (mode === 'single')
    return (
      <Combobox
        {...props}
        mode="single"
        value={value}
        items={absenceStatusItems}
        clearable
        noSearch
        placeholder={placeholder ?? translations.single.placeholder}
        onChange={onChange}
      />
    )

  return (
    <Combobox
      {...props}
      mode="multiple"
      value={value}
      items={absenceStatusItems}
      noSearch
      placeholder={placeholder ?? translations.multiple.placeholder}
      onChange={onChange}
    />
  )
}
